import { z } from "zod/v4";

import { ExpressionNodeOperator } from "./audiences";
import { ProductTargetingExpressionNode } from "./experience-product-targeting";
import { OfferProductInclusionTypeList } from "./offer-products";

export const CartTargetingAttributeList = [
  "anything",
  "productId",
  "collection",
  "tag",
  "cartSubtotal",
  "cartItemCount",
  "subscription",
] as const;

export type CartTargetingAttribute =
  (typeof CartTargetingAttributeList)[number];

export const CartTargetingFilterList = [
  "includes",
  "doesNotInclude",
  "greaterThan",
  "lessThan",
  "equalTo",
  "greaterThanOrEqual",
  "lessThanOrEqual",
  "between",
] as const;

export type CartTargetingFilter = (typeof CartTargetingFilterList)[number];

export const CartTargetingProductVariant = z.record(
  z.string(), // productId
  z.array(
    z.object({
      variantId: z.string(),
      inclusionType: z.enum(OfferProductInclusionTypeList),
    })
  )
);

export const CartTargetingExpressionNodeQuery = z.object({
  key: z.enum(CartTargetingAttributeList),
  type: z.enum(CartTargetingAttributeList),
  filter: z.enum(CartTargetingFilterList),
  value: z
    .union([z.string(), z.number(), CartTargetingProductVariant])
    .optional(),
});

export type CartTargetingExpressionNodeQueryType = z.infer<
  typeof CartTargetingExpressionNodeQuery
>;

export const CartTargetingNode = z
  .object({
    query: CartTargetingExpressionNodeQuery.optional(),
    operator: ExpressionNodeOperator.optional(),
  })
  .refine(
    (val) => (val.operator && !val.query) || (!val.operator && val.query),
    { message: "must have one of 'operator' or 'query', and not both" }
  );

export type CartTargetingNodeType = z.infer<typeof CartTargetingNode>;

export const CartTargetingSchema = z.object({
  id: z.uuid(),
  expression: z.array(CartTargetingNode),
});

export type CartTargetingDto = z.infer<typeof CartTargetingSchema>;

export const CreateCartTargetingDtoSchema = CartTargetingSchema.extend({
  id: z.uuid().optional(),
});

export type CreateCartTargetingDto = z.infer<
  typeof CreateCartTargetingDtoSchema
>;

export const CartItemSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  quantity: z.number(),
  /**
   * The UNIT price, in MAJOR units. Every producer emits this one meaning.
   *
   * A surface that cannot observe a unit price DERIVES it by dividing
   * `lineTotal` by `quantity`, which is the lossy direction — see `lineTotal`.
   * Checkout is that surface: its `CartLineCost` exposes only `totalAmount`,
   * with no `amountPerQuantity` to read.
   */
  price: z.number(),
  /**
   * The line total, in MAJOR units, when the producer observes it EXACTLY:
   * `/cart.js` `final_line_price`, checkout `cost.totalAmount`, an order line's
   * `totalPriceSet`.
   *
   * ABSENT means the producer had no exact figure and the reader should derive
   * `price * quantity`. Carrying it matters because checkout has no unit price
   * to send: it divides, and the division rounds, so re-multiplying that
   * rounded unit price would not give back the money the buyer actually pays.
   */
  lineTotal: z.number().optional(),
  collections: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  isSubscriptionLine: z.boolean().optional(),
  /**
   * The variant's chosen options, when the producer knows them. Checkout does;
   * the AJAX cart does not, so this stays optional and absent means "unknown"
   * rather than "none".
   */
  selectedOptions: z
    .array(z.object({ name: z.string(), value: z.string() }))
    .optional(),
  /**
   * IG line markers ONLY — the line's properties (Ajax) or attributes
   * (checkout) filtered to keys starting with `_ig`. See `IG_LINE_MARKERS`.
   *
   * Merchant line properties (engraving text, gift messages, custom keys) are
   * deliberately NOT forwarded: they are shopper PII and nothing server-side
   * reads them. The producer filters; the server interprets. A producer never
   * decides what a marker MEANS, so the marker table can change without
   * shipping a new plugin or extension build.
   *
   * ABSENT means the producer sends no markers at all — an older client, or a
   * surface with no attribute concept. `{}` means it looked and found none.
   * That distinction is exactly what makes a provenance flag `null` rather
   * than `false`, so do not default this to an empty object anywhere.
   */
  igLineMarkers: z.record(z.string(), z.string()).optional(),
  /**
   * Shopify Ajax `item.gift_card`. Checkout exposes no equivalent marker on a
   * cart line, so it stays absent there and the flag reads `null` rather than
   * claiming the line is not a gift card.
   */
  giftCard: z.boolean().optional(),
  /**
   * Structural, not interpretive: this line is a component of an expanded
   * bundle parent. Only checkout can see the nesting (`lineComponents`);
   * `/cart.js` never lists component lines, so the plugin sends `false` — a
   * real answer — rather than omitting it.
   */
  bundleChild: z.boolean().optional(),
});

export type CartItem = z.infer<typeof CartItemSchema>;

export const CartDataSchema = z.object({
  items: z.array(CartItemSchema),
  subtotal: z.number(),
  itemCount: z.number(),
});

export type CartData = z.infer<typeof CartDataSchema>;
